import './AutoComplete.css'

import clsx from 'clsx'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AngleIcon, CloseIcon } from '../../../components/Icons'
import { useDebounce } from '../../../hooks/useDebounce'

export interface AutoCompleteOption {
  value: string | number
  label: string
  disabled?: boolean
  [key: string]: unknown
}

type AutoCompleteValue<isMultiple extends boolean> = isMultiple extends true
  ? AutoCompleteOption[]
  : AutoCompleteOption

export interface AutoCompleteProps<isMultiple extends boolean = false> {
  name?: string
  label?: string
  value?: AutoCompleteValue<isMultiple> | null
  onChange: (selected: AutoCompleteValue<isMultiple> | null) => void
  placeholder?: string
  disabled?: boolean
  required?: boolean
  error?: string
  options?: AutoCompleteOption[]
  onSearch?: (
    query: string,
  ) => Promise<AutoCompleteOption[]> | AutoCompleteOption[]
  multiple?: isMultiple
  allowClear?: boolean
  closeOnSelect?: boolean
  minSearchLength?: number
  debounceMs?: number
  searchable?: boolean
  selectOnly?: boolean
  freeSolo?: boolean
  className?: string
  inputClassName?: string
  dropdownClassName?: string
  optionClassName?: string
  loadingText?: string
  noOptionsText?: string
  searchPromptText?: string
  renderOption?: (
    option: AutoCompleteOption,
    isSelected: boolean,
  ) => React.ReactNode
  renderSelected?: (option: AutoCompleteOption) => React.ReactNode
  onFocus?: () => void
  onBlur?: () => void
  onDropdownToggle?: (isOpen: boolean) => void
  needFetchNextPage?: boolean
  fetchNextPage?: () => void
  isLoading?: boolean
}

export const AutoComplete = <isMultiple extends boolean = false>({
  name,
  label,
  value,
  onChange,
  placeholder = 'Select option...',
  disabled = false,
  required = false,
  error,
  options = [],
  onSearch,
  multiple = false as isMultiple,
  allowClear = true,
  closeOnSelect = true,
  minSearchLength = 0,
  debounceMs = 300,
  searchable = true,
  selectOnly = false,
  freeSolo = false,
  className = '',
  inputClassName = '',
  dropdownClassName = '',
  optionClassName = '',
  loadingText = 'Loading...',
  noOptionsText = 'No options found',
  searchPromptText = 'Type to search...',
  renderOption,
  renderSelected,
  onFocus,
  onBlur,
  onDropdownToggle,
  needFetchNextPage,
  fetchNextPage,
  isLoading = false,
}: AutoCompleteProps<isMultiple>) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [filteredOptions, setFilteredOptions] =
    useState<AutoCompleteOption[]>(options)
  const [loading, setLoading] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState(-1)

  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const dropdownContainerRef = useRef<HTMLDivElement>(null)
  const isCalled = React.useRef<IntersectionObserverEntry | null>(null)

  const debouncedSearchQuery = useDebounce(searchQuery, debounceMs)

  const isSearchable = (searchable && !selectOnly) || freeSolo

  const createNewOption = useCallback((value: string): AutoCompleteOption => {
    return { value, label: `Add "${value}"` }
  }, [])

  const selectedOptions = useMemo((): AutoCompleteOption[] => {
    if (!value) return []
    return Array.isArray(value) ? value : [value]
  }, [value])

  const isSelected = useCallback(
    (option: AutoCompleteOption): boolean => {
      if (!value) return false
      if (multiple && Array.isArray(value)) {
        return value.some(v => v.value === option.value)
      }
      return !Array.isArray(value) && value.value === option.value
    },
    [value, multiple],
  )

  const handleSelect = useCallback(
    (option: AutoCompleteOption) => {
      if (option.disabled) return

      if (multiple) {
        const currentSelected = selectedOptions
        const isAlreadySelected = isSelected(option)

        if (isAlreadySelected) {
          const newSelected = currentSelected.filter(
            v => v.value !== option.value,
          )
          onChange(
            (newSelected?.length > 0
              ? newSelected
              : null) as AutoCompleteValue<isMultiple>,
          )
        } else {
          onChange([
            ...currentSelected,
            option,
          ] as AutoCompleteValue<isMultiple>)
        }
      } else {
        onChange(option as AutoCompleteValue<isMultiple>)
        if (closeOnSelect) {
          setIsOpen(false)
        }
        setSearchQuery('')
      }

      setFocusedIndex(-1)
    },
    [multiple, selectedOptions, isSelected, onChange, closeOnSelect],
  )

  const handleRemove = useCallback(
    (option: AutoCompleteOption, e: React.MouseEvent) => {
      e.stopPropagation()
      if (!multiple) return

      const currentSelected = selectedOptions
      const newSelected = currentSelected.filter(v => v.value !== option.value)
      onChange(
        (newSelected.length > 0
          ? newSelected
          : null) as AutoCompleteValue<isMultiple>,
      )
    },
    [multiple, selectedOptions, onChange],
  )

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onChange(null)
      setSearchQuery('')
      setFocusedIndex(-1)
    },
    [onChange],
  )

  const optionsRef = useRef(options)
  const onSearchRef = useRef(onSearch)

  useEffect(() => {
    optionsRef.current = options

    if (freeSolo && searchQuery.trim().length > 0) {
      const exactFound = options.some(
        opt =>
          String(opt.value).toLowerCase() === searchQuery.trim().toLowerCase(),
      )
      if (exactFound) {
        setFilteredOptions(options)
        return
      }
      const itemOnFreeSolo = [createNewOption(searchQuery.trim()), ...options]
      setFilteredOptions(itemOnFreeSolo)
      setFocusedIndex(0)
    } else {
      setFilteredOptions(options)
    }
  }, [options])

  useEffect(() => {
    onSearchRef.current = onSearch
  }, [onSearch])

  useEffect(() => {
    if (!isSearchable) {
      setFilteredOptions(optionsRef.current)
      return
    }

    const performSearch = async () => {
      if (debouncedSearchQuery.length >= minSearchLength) {
        const currentOnSearch = onSearchRef.current
        const currentOptions = optionsRef.current

        if (currentOnSearch) {
          setLoading(true)
          try {
            let results = await currentOnSearch(debouncedSearchQuery)
            const itemOnFreeSolo =
              freeSolo && searchQuery.trim()
                ? [createNewOption(searchQuery.trim())]
                : []
            results = Array.isArray(results) ? results : itemOnFreeSolo

            setFilteredOptions(results)
          } catch (error) {
            console.error('Search error:', error)
            setFilteredOptions([])
          } finally {
            setLoading(false)
          }
        } else {
          const filtered = currentOptions.filter(option =>
            option.label
              .toLowerCase()
              .includes(debouncedSearchQuery.toLowerCase()),
          )
          const itemOnFreeSolo =
            freeSolo && searchQuery.trim()
              ? [createNewOption(searchQuery.trim())]
              : []

          const filteredResult = filtered.length ? filtered : itemOnFreeSolo
          setFilteredOptions(filteredResult)
        }
      } else if (debouncedSearchQuery.length === 0) {
        setFilteredOptions(optionsRef.current)
      }
    }

    void performSearch()
  }, [debouncedSearchQuery, minSearchLength, isSearchable])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen) {
        if (
          e.key === 'Enter' ||
          e.key === 'ArrowDown' ||
          e.key === 'ArrowUp' ||
          e.key === ' '
        ) {
          e.preventDefault()
          setIsOpen(true)
          return
        }
      }

      const optionsToUse = isSearchable ? filteredOptions : options

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setFocusedIndex(prev =>
            prev < optionsToUse.length - 1 ? prev + 1 : 0,
          )
          break
        case 'ArrowUp':
          e.preventDefault()
          setFocusedIndex(prev =>
            prev > 0 ? prev - 1 : optionsToUse.length - 1,
          )
          break
        case 'Enter':
          e.preventDefault()
          if (focusedIndex >= 0 && focusedIndex < optionsToUse.length) {
            handleSelect(optionsToUse[focusedIndex])
          }
          break
        case 'Escape':
          setIsOpen(false)
          setFocusedIndex(-1)
          inputRef.current?.blur()
          break
        case 'Tab':
          setIsOpen(false)
          setFocusedIndex(-1)
          break
      }
    },
    [
      isOpen,
      filteredOptions,
      options,
      focusedIndex,
      handleSelect,
      isSearchable,
    ],
  )

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false)
      setFocusedIndex(-1)
    }
  }, [])

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [handleClickOutside])

  useEffect(() => {
    onDropdownToggle?.(isOpen)
  }, [isOpen, onDropdownToggle])

  useEffect(() => {
    if (selectOnly) {
      setSearchQuery('')
    }
  }, [selectOnly])

  useEffect(() => {
    const el = dropdownContainerRef.current
    if (!el || !isOpen || !needFetchNextPage) return

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0]
        if (
          entry.isIntersecting &&
          !isCalled.current?.target.isSameNode(entry.target)
        ) {
          isCalled.current = entry
          fetchNextPage?.()
          observer.unobserve(entry.target)
          if (el.lastChild && el.lastChild !== entry.target) {
            observer.observe(el.lastChild as Element)
          }
        }
      },
      {
        root: el,
        threshold: 1.0,
      },
    )

    if (el.lastChild) {
      observer.observe(el.lastChild as Element)
    }

    return () => {
      observer.disconnect()
    }
  }, [isOpen, needFetchNextPage, fetchNextPage, filteredOptions])

  const hasValue = selectedOptions.length > 0
  const optionsToShow = isSearchable ? filteredOptions : options

  const dropdownContent = useMemo(() => {
    if (!isOpen) return null

    return optionsToShow?.map((option, index) => {
      const selected = isSelected(option)
      const focused = index === focusedIndex

      return (
        <div
          key={option.value}
          onClick={() => !option.disabled && handleSelect(option)}
          onMouseEnter={() => setFocusedIndex(index)}
          className={clsx(
            'tc-mv-autocomplete-option',
            {
              selected,
              focused,
              disabled: option.disabled,
            },
            optionClassName,
          )}
        >
          {renderOption ? (
            renderOption(option, selected)
          ) : (
            <span className={option.disabled ? 'text-gray-400' : ''}>
              {option.label}
            </span>
          )}
        </div>
      )
    })
  }, [
    isOpen,
    optionsToShow,
    isSelected,
    focusedIndex,
    handleSelect,
    optionClassName,
    renderOption,
  ])

  const selectedTags = useMemo(() => {
    if (!multiple || !hasValue) return null

    return selectedOptions.map(option => (
      <span key={option.value} className="tc-mv-autocomplete-tag">
        {renderSelected ? renderSelected(option) : option.label}
        {!disabled && (
          <button
            type="button"
            onClick={e => handleRemove(option, e)}
            className="tc-mv-autocomplete-tag-remove"
          >
            <CloseIcon />
          </button>
        )}
      </span>
    ))
  }, [
    multiple,
    hasValue,
    selectedOptions,
    renderSelected,
    disabled,
    handleRemove,
  ])

  return (
    <div className="tc-mv-autocomplete-container" ref={dropdownRef}>
      {/* Label */}
      {label && (
        <label htmlFor={name} className="tc-mv-autocomplete-label">
          {label}
          {required && <span className="tc-mv-autocomplete-required">*</span>}
        </label>
      )}

      {/* Main Input Container */}
      <div
        className={clsx(
          'tc-mv-autocomplete-selectbox',
          {
            disabled,
            error: !!error,
          },
          className,
        )}
        onClick={() => {
          if (!disabled) {
            setIsOpen(!isOpen)
            if (isSearchable) {
              inputRef.current?.focus()
            }
          }
        }}
        onKeyDown={!isSearchable ? handleKeyDown : undefined}
        tabIndex={!isSearchable ? 0 : -1}
      >
        {/* Selected Items Display */}
        <div className="tc-mv-autocomplete-content">
          {/* Multiple selection tags */}
          {selectedTags}

          {/* Input field for searchable or placeholder for non-searchable */}
          {isSearchable ? (
            <input
              ref={inputRef}
              type="text"
              id={name}
              name={name}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                setIsOpen(true)
                onFocus?.()
              }}
              onBlur={onBlur}
              onClick={e => e.stopPropagation()}
              placeholder={
                !multiple && hasValue
                  ? renderSelected
                    ? (renderSelected(selectedOptions[0]) as string)
                    : selectedOptions[0].label
                  : placeholder
              }
              disabled={disabled}
              required={required}
              className={clsx(
                'tc-mv-autocomplete-input',
                {
                  'has-value': !multiple && hasValue,
                },
                inputClassName,
              )}
              autoComplete="off"
            />
          ) : /* Non-searchable display */
          hasValue && !multiple ? (
            <span className="tc-mv-autocomplete-selected-value">
              {renderSelected
                ? renderSelected(selectedOptions[0])
                : selectedOptions[0].label}
            </span>
          ) : (
            <span className="tc-mv-autocomplete-placeholder">
              {placeholder}
            </span>
          )}
        </div>

        {/* Control Icons */}
        <div className="tc-mv-autocomplete-controls">
          {(loading || isLoading) && (
            <div className="tc-mv-autocomplete-loading-icon">
              <i className="fa-solid fa-spinner fa-spin text-white" />
            </div>
          )}
          {hasValue && allowClear && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="tc-mv-autocomplete-clear-btn"
            >
              <CloseIcon />
            </button>
          )}
          <span className={clsx('tc-mv-autocomplete-arrow', { open: isOpen })}>
            <AngleIcon />
          </span>
        </div>
      </div>

      {/* Error Message */}
      {error && <p className="tc-mv-autocomplete-error">{error}</p>}

      {/* Dropdown */}
      {isOpen && (
        <div className={clsx('tc-mv-autocomplete-dropdown', dropdownClassName)}>
          <div
            ref={dropdownContainerRef}
            className="tc-mv-autocomplete-dropdown-content space-y-1"
          >
            {dropdownContent}
            {(loading || isLoading) && (
              <div className="tc-mv-autocomplete-loading">{loadingText}</div>
            )}
            {!freeSolo &&
              !loading &&
              !isLoading &&
              optionsToShow.length < 1 && (
                <div className="tc-mv-autocomplete-no-options">
                  {isSearchable && searchQuery.length < minSearchLength
                    ? searchPromptText
                    : noOptionsText}
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  )
}
